import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Inbox, Loader2, Lock, LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePageTitle } from "@/lib/site";
import { AdminAnalytics } from "@/components/AdminAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const th = "px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500";
const td = "px-4 py-4 align-top text-sm text-zinc-700";

export default function Admin() {
  usePageTitle("Admin — CalCor Solutions");
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("leads");
  const [loadingData, setLoadingData] = useState(false);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [l, b] = await Promise.all([
        axios.get(`${API}/admin/leads`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
      ]);
      setLeads(l.data);
      setBookings(b.data);
    } catch {
      toast.error("Could not load submissions. Please sign in again.");
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      toast.success("Signed in.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  if (user === null) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center pt-20">
        <Loader2 className="h-6 w-6 animate-spin text-pine" />
      </section>
    );
  }

  if (user === false) {
    return (
      <section data-testid="admin-login" className="flex min-h-[80vh] items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md rounded-3xl border border-black/10 bg-white p-10">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-pine/10">
            <Lock className="h-5 w-5 text-pine" />
          </span>
          <h1 className="mt-6 font-serif text-3xl tracking-tight text-ink">Owner sign-in</h1>
          <p className="mt-2 text-sm text-zinc-600">
            Private area for CalCor Solutions. Submissions from the website live here.
          </p>
          <form onSubmit={login} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
                Email
              </Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-black/15 bg-white focus-visible:ring-pine"
                placeholder="you@calcorsolutions.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
                Password
              </Label>
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl border-black/15 bg-white focus-visible:ring-pine"
                placeholder="••••••••••••"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm font-medium text-clay">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="w-full rounded-full bg-pine py-6 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </section>
    );
  }

  const rows = tab === "leads" ? leads : bookings;

  return (
    <section data-testid="admin-dashboard" className="mx-auto max-w-7xl px-6 pt-32 pb-24 lg:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            <span className="h-px w-10 bg-pine" />
            Private dashboard
          </p>
          <h1 className="mt-4 font-serif text-4xl tracking-tighter text-ink sm:text-5xl">
            Website <em className="text-pine">submissions.</em>
          </h1>
          <p className="mt-3 text-sm text-zinc-600">
            Signed in as {user.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={loadData}
            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-pine hover:text-pine"
          >
            <RefreshCw className={`h-4 w-4 ${loadingData ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-clay hover:text-clay"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <div className="inline-flex rounded-full border border-black/10 bg-white p-1">
          <button
            data-testid="admin-tab-leads"
            onClick={() => setTab("leads")}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "leads" ? "bg-pine text-white" : "text-zinc-600 hover:text-pine"
            }`}
          >
            Consultation requests ({leads.length})
          </button>
          <button
            data-testid="admin-tab-bookings"
            onClick={() => setTab("bookings")}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "bookings" ? "bg-pine text-white" : "text-zinc-600 hover:text-pine"
            }`}
          >
            Booking requests ({bookings.length})
          </button>
          <button
            data-testid="admin-tab-analytics"
            onClick={() => setTab("analytics")}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "analytics" ? "bg-pine text-white" : "text-zinc-600 hover:text-pine"
            }`}
          >
            Analytics
          </button>
        </div>
        {tab !== "analytics" && (
        <a
          href={tab === "leads" ? `${API}/admin/export/leads` : `${API}/admin/export/bookings`}
          data-testid="admin-download-csv-button"
          className="group inline-flex items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
        >
          <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          Download CSV
        </a>
        )}
      </div>

      {tab === "analytics" && <AdminAnalytics />}
      <div className={`mt-6 overflow-hidden rounded-3xl border border-black/10 bg-white ${tab === "analytics" ? "hidden" : ""}`}>
        {rows.length === 0 ? (
          <div data-testid="admin-empty-state" className="flex flex-col items-center gap-3 py-20 text-center">
            <Inbox className="h-8 w-8 text-pine/40" />
            <p className="font-serif text-xl text-ink">Nothing here yet</p>
            <p className="max-w-sm text-sm text-zinc-500">
              New {tab === "leads" ? "consultation" : "booking"} requests from the website will appear here the moment they arrive.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {tab === "leads" ? (
              <table data-testid="admin-leads-table" className="w-full min-w-[900px] border-collapse">
                <thead className="border-b border-black/10 bg-paper/60">
                  <tr>
                    <th className={th}>Received</th>
                    <th className={th}>Name</th>
                    <th className={th}>Contact</th>
                    <th className={th}>Client type</th>
                    <th className={th}>Service</th>
                    <th className={th}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className="border-b border-black/5 last:border-0 hover:bg-paper/50">
                      <td className={`${td} whitespace-nowrap text-zinc-500`}>{fmt(l.created_at)}</td>
                      <td className={`${td} font-semibold text-ink`}>
                        {l.name}
                        {l.organization && <span className="block text-xs font-normal text-zinc-500">{l.organization}</span>}
                      </td>
                      <td className={td}>
                        <a href={`mailto:${l.email}`} className="text-pine hover:text-clay">{l.email}</a>
                        {l.phone && <span className="block text-xs text-zinc-500">{l.phone}</span>}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{l.client_type}</td>
                      <td className={`${td} whitespace-nowrap`}>{l.service}</td>
                      <td className={`${td} max-w-xs`}>{l.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table data-testid="admin-bookings-table" className="w-full min-w-[900px] border-collapse">
                <thead className="border-b border-black/10 bg-paper/60">
                  <tr>
                    <th className={th}>Received</th>
                    <th className={th}>Name</th>
                    <th className={th}>Contact</th>
                    <th className={th}>Service</th>
                    <th className={th}>Preferred time</th>
                    <th className={th}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.booking_id} data-testid={`booking-row-${b.booking_id}`} className="border-b border-black/5 last:border-0 hover:bg-paper/50">
                      <td className={`${td} whitespace-nowrap text-zinc-500`}>{fmt(b.created_at)}</td>
                      <td className={`${td} font-semibold text-ink`}>{b.name}</td>
                      <td className={td}>
                        <a href={`mailto:${b.email}`} className="text-pine hover:text-clay">{b.email}</a>
                        {b.phone && <span className="block text-xs text-zinc-500">{b.phone}</span>}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{b.service}</td>
                      <td className={`${td} whitespace-nowrap`}>
                        {b.preferred_date}
                        <span className="block text-xs text-zinc-500">{b.time_window}</span>
                      </td>
                      <td className={`${td} max-w-xs`}>{b.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
